import React, { createContext, useContext, useState, useEffect } from 'react';

const UserPreferencesContext = createContext();

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

export const UserPreferencesProvider = ({ children }) => {
  const [userMode, setUserMode] = useState('simple'); // 'simple' or 'advanced'
  const [preferences, setPreferences] = useState({
    theme: 'dark',
    notifications: true,
    autoSave: true,
    showTutorials: true,
  });

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('boneforge_user_mode');
    const savedPreferences = localStorage.getItem('boneforge_preferences');
    
    if (savedMode) {
      setUserMode(savedMode);
    }
    
    if (savedPreferences) {
      try {
        setPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    }
  }, []);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('boneforge_user_mode', userMode);
  }, [userMode]);

  useEffect(() => {
    localStorage.setItem('boneforge_preferences', JSON.stringify(preferences));
  }, [preferences]);

  const toggleUserMode = () => {
    setUserMode(prev => prev === 'simple' ? 'advanced' : 'simple');
  };

  const updatePreference = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetPreferences = () => {
    setUserMode('simple');
    setPreferences({
      theme: 'dark',
      notifications: true,
      autoSave: true,
      showTutorials: true,
    });
    localStorage.removeItem('boneforge_user_mode');
    localStorage.removeItem('boneforge_preferences');
  };

  const value = {
    userMode,
    setUserMode,
    toggleUserMode,
    preferences,
    updatePreference,
    resetPreferences,
    isAdvancedMode: userMode === 'advanced',
    isSimpleMode: userMode === 'simple',
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export default UserPreferencesProvider;
